/**
 * Homestead piece bake: catalog compile cache + PURE placed-piece expansion,
 * feeding the SAME instanced bake as settlement buildings (bakeBuildingMeshes).
 *
 * Two halves (the buildingMeshes.ts convention):
 *  - PURE (node-testable, no THREE):
 *      compiledPieces()  — memoized compileAsset() over every PIECE_CATALOG doc
 *      pieceInstances()  — Piece[] → flat BuildingInstance[] (planet-local matrices)
 *  - THREE-dependent:
 *      bakePieceMeshes() — thin re-export of bakeBuildingMeshes (identical buckets)
 *
 * Transform contract: M_world = T(p) · R(q) × compiled.transform — pieces are
 * FREELY placed (full quaternion, the Valheim snap+free decision), so unlike
 * buildings there is no tangent-frame basis or quarter-turn table; `q` already
 * encodes the surface alignment the build controller chose. Matrices are
 * PLANET-LOCAL — the baked meshes parent to planet.group (markers precedent).
 *
 * Determinism: same Piece[] → byte-identical instance list (pieces in array
 * order; per piece the compiled instances in doc order).
 */
import {
  PIECE_CATALOG,
  compileAsset,
  type CompiledAsset,
  type Piece,
} from "@hyperlight/shared";
import { bakeBuildingMeshes, type BuildingInstance } from "../world/settlement/buildingMeshes.js";

// ── catalog compile cache ─────────────────────────────────────────────────────

let pieceCache: ReadonlyMap<string, CompiledAsset> | null = null;

/** All PIECE_CATALOG docs compiled once per session, keyed by piece id. */
export function compiledPieces(): ReadonlyMap<string, CompiledAsset> {
  if (!pieceCache) {
    const m = new Map<string, CompiledAsset>();
    for (const entry of PIECE_CATALOG) m.set(entry.id, compileAsset(entry.doc));
    pieceCache = m;
  }
  return pieceCache;
}

// ── pure expansion ────────────────────────────────────────────────────────────

/** out = a × b, 16-element column-major (local copy — buildingMeshes' is private). */
function mat4Multiply(a: number[], b: number[]): number[] {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      out[col * 4 + row] =
        a[row] * b[col * 4] + a[4 + row] * b[col * 4 + 1]
        + a[8 + row] * b[col * 4 + 2] + a[12 + row] * b[col * 4 + 3];
    }
  }
  return out;
}

/** T(p) · R(q) as a column-major mat4 (THREE's makeRotationFromQuaternion layout). */
function poseMatrix(p: [number, number, number], q: [number, number, number, number]): number[] {
  const [x, y, z, w] = q;
  const x2 = x + x, y2 = y + y, z2 = z + z;
  const xx = x * x2, xy = x * y2, xz = x * z2;
  const yy = y * y2, yz = y * z2, zz = z * z2;
  const wx = w * x2, wy = w * y2, wz = w * z2;
  return [
    1 - (yy + zz), xy + wz, xz - wy, 0,
    xy - wz, 1 - (xx + zz), yz + wx, 0,
    xz + wy, yz - wx, 1 - (xx + yy), 0,
    p[0], p[1], p[2], 1,
  ];
}

/** Per-piece body tints selected by `Piece.mat` (applied to material-0 instances
 *  only — metal/accent/glass read as authored). Index clamps to 0. */
const HS_TINTS: readonly [number, number, number][] = [
  [1, 1, 1],            // 0 neutral
  [1.06, 0.98, 0.88],   // 1 warm
  [0.92, 0.97, 1.06],   // 2 cool
  [0.72, 0.72, 0.74],   // 3 dark
];

function geomKeyOf(inst: { kind: string; params: number[]; profile?: unknown; deforms?: unknown }): string {
  return JSON.stringify([inst.kind, inst.params, inst.profile ?? null, inst.deforms ?? null]);
}

/**
 * Expand placed pieces into a flat, deterministic BuildingInstance list (PURE —
 * plain arrays end-to-end). Unknown catalog indices are SKIPPED, never thrown:
 * a newer server may stream pieces this client build doesn't know yet, and one
 * alien piece must not kill the whole homestead bake.
 */
export function pieceInstances(pieces: readonly Piece[]): BuildingInstance[] {
  const out: BuildingInstance[] = [];
  const compiled = compiledPieces();
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i];
    const entry = PIECE_CATALOG[piece.t];
    const asset = entry ? compiled.get(entry.id) : undefined;
    if (!entry || !asset) continue;
    const mP = poseMatrix(piece.p, piece.q);
    const tint = HS_TINTS[piece.mat] ?? HS_TINTS[0];
    const mats = entry.doc.materials;
    for (const inst of asset.instances) {
      const base = mats[inst.material]?.color ?? 0xffffff;
      const r = ((base >> 16) & 0xff) / 255;
      const g = ((base >> 8) & 0xff) / 255;
      const b = (base & 0xff) / 255;
      const body = inst.material === 0; // only the body slot takes the player tint
      const bi: BuildingInstance = {
        b: i, // piece index — a future picker maps instance → placed piece
        geomKey: geomKeyOf(inst),
        kind: inst.kind,
        params: [...inst.params],
        glass: inst.material === 3,
        footstepSurface: mats[inst.material]?.metalness > .3 ? 'metal'
          : inst.material === 0 ? (entry.tier === 'glass' ? 'stone' : entry.tier)
          : inst.material === 4 ? 'wood' : 'stone',
        matrix: mat4Multiply(mP, inst.transform),
        color: body ? [r * tint[0], g * tint[1], b * tint[2]] : [r, g, b],
      };
      if (inst.profile) bi.profile = inst.profile.map((p) => [p[0], p[1]] as [number, number]);
      if (inst.deforms) bi.deforms = { ...inst.deforms };
      out.push(bi);
    }
  }
  return out;
}

// ── THREE-dependent bake ──────────────────────────────────────────────────────

/** Identical bucketing/material conventions as settlement buildings — glass
 *  shares the night-poke MeshBasic, bodies share one Lambert w/ setColorAt. */
export const bakePieceMeshes = bakeBuildingMeshes;
